import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { buildHttpParams } from '../../../core/utils/http-util';
import { environment } from '../../../../environments/environment';
import { endpoints } from '../../core/constants/endpoints';
import {
  IAuthorization,
  IAuthorizationRaw,
  IAuthorizationStatus,
  IAuthorizationStatusRaw,
  IConnection,
  IConnectionRaw,
  ICreateConnectionRequest,
  IReconnectRequest,
  IUpdateConnectionRequest,
  toAuthorization,
  toAuthorizationStatus,
  toConnection,
  toCreateConnectionRequestRaw,
  toReconnectRequestRaw,
  toUpdateConnectionRequestRaw
} from '../interfaces/connection.interface';
import {
  IConnector,
  IConnectorCatalogPage,
  IConnectorCatalogPageRaw,
  IConnectorCategoriesRaw,
  IConnectorRaw,
  toConnector,
  toConnectorCatalogPage,
  toConnectorCategories
} from '../interfaces/connector.interface';

export interface IConnectorCatalogFilter {
  search?: string;
  category?: string;
  page?: number;
  pageSize?: number;
}

@Injectable({ providedIn: 'root' })
export class ConnectorsApi {
  private readonly baseUrl = `${environment.ceppemAiApi}/${endpoints.ceppemAi.connectors}`;

  constructor(private http: HttpClient) {}

  getCatalog(filter?: IConnectorCatalogFilter): Observable<IConnectorCatalogPage> {
    const params = buildHttpParams({
      search: filter?.search,
      category: filter?.category,
      page: filter?.page,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      page_size: filter?.pageSize
    });

    return this.http
      .get<IConnectorCatalogPageRaw>(`${this.baseUrl}/catalog`, { params })
      .pipe(map(toConnectorCatalogPage));
  }

  getCategories(): Observable<string[]> {
    return this.http
      .get<IConnectorCategoriesRaw>(`${this.baseUrl}/catalog/categories`)
      .pipe(map(toConnectorCategories));
  }

  getCatalogItem(slug: string): Observable<IConnector> {
    return this.http
      .get<IConnectorRaw>(`${this.baseUrl}/catalog/${slug}`)
      .pipe(map(toConnector));
  }

  getConnections(): Observable<IConnection[]> {
    return this.http
      .get<IConnectionRaw[]>(`${this.baseUrl}/connections`)
      .pipe(map(raw => raw.map(toConnection)));
  }

  createConnection(slug: string, request: ICreateConnectionRequest): Observable<IAuthorization> {
    return this.http
      .post<IAuthorizationRaw>(
        `${this.baseUrl}/${slug}/connections`,
        toCreateConnectionRequestRaw(request)
      )
      .pipe(map(toAuthorization));
  }

  updateConnection(
    accountId: string,
    request: IUpdateConnectionRequest
  ): Observable<IConnection> {
    return this.http
      .patch<IConnectionRaw>(
        `${this.baseUrl}/connections/${accountId}`,
        toUpdateConnectionRequestRaw(request)
      )
      .pipe(map(toConnection));
  }

  deleteConnection(accountId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/connections/${accountId}`);
  }

  reconnect(accountId: string, request: IReconnectRequest): Observable<IAuthorization> {
    return this.http
      .post<IAuthorizationRaw>(
        `${this.baseUrl}/connections/${accountId}/reconnect`,
        toReconnectRequestRaw(request)
      )
      .pipe(map(toAuthorization));
  }

  getAuthorizationStatus(accountId: string): Observable<IAuthorizationStatus> {
    return this.http
      .get<IAuthorizationStatusRaw>(
        `${this.baseUrl}/connections/${accountId}/authorization-status`
      )
      .pipe(map(toAuthorizationStatus));
  }

  cancelAuthorization(accountId: string): Observable<IAuthorizationStatus> {
    return this.http
      .post<IAuthorizationStatusRaw>(
        `${this.baseUrl}/connections/${accountId}/authorization/cancel`,
        {}
      )
      .pipe(map(toAuthorizationStatus));
  }
}
